import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../api';
import Topbar from '../../Tools/Topbar';
import LeftSidebar from '../../Tools/LeftSidebar';
import Settings from '../../Tools/Setting/Settings';
import useSettings from '../../Tools/Setting/useSettings';
import PatientSituationtransfercaseModal from '../TransferCasesTab/TransferCaseModal/PatientSituationtransfercase';
import { format } from 'date-fns';

const DateTimeFormatFetcher = () => {
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axiosInstance.get('/ems/get-system-setting', {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('auth_token')}` },
        });
        if (res.data?.status === 'success' && res.data.data.length > 0) {
          const { date_format = 'DD/MM/YYYY', time_format = '24' } = res.data.data[0];
          sessionStorage.setItem('date_format', date_format);
          sessionStorage.setItem('time_format', time_format);
        }
      } catch (err) {
        sessionStorage.setItem('date_format', 'DD/MM/YYYY');
        sessionStorage.setItem('time_format', '24');
      }
    };
    fetchSettings();
  }, []);
  return null;
};

const TransferCaseFinalApprovalDetails = () => {
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);

  // Final Approval (Requester) States
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const caseId = sessionStorage.getItem('currentCaseId');
  const settingsRef = useRef();
  const { settings, updateSetting, handleColorTheme } = useSettings();

  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);

  // Utility function to format date based on settings
      const formatCaseDate = (dateString) => {
      if (!dateString) return 'N/A';
      try {
        const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
        const timeFormat = sessionStorage.getItem('time_format') || '24';
    
        // Map WordPress-style formats to date-fns syntax
        const dateFormatMap = {
          'DD/MM/YYYY': 'dd/MM/yyyy',
          'MM/DD/YYYY': 'MM/dd/yyyy',
          'YYYY-MM-DD': 'yyyy-MM-dd',
        };
    
        const timeFormatString = timeFormat === '12' ? 'hh:mm:ss a' : 'HH:mm:ss';
        const formatString = `${dateFormatMap[dateFormat] || 'dd/MM/yyyy'} ${timeFormatString}`;
    
        // ✅ Parse both ISO and non-ISO timestamps as *UTC*, not local time
        let utcDate;
        if (dateString.includes('T')) {
          // ISO 8601 with timezone (e.g. 2025-10-27T16:27:19.000000Z)
          utcDate = new Date(dateString);
        } else {
          // MySQL-like (e.g. 2025-10-27 16:35:40)
          utcDate = new Date(dateString.replace(' ', 'T') + 'Z'); // force UTC
        }
    
        // Format without applying local timezone
        const utcTimestamp = new Date(
          utcDate.getUTCFullYear(),
          utcDate.getUTCMonth(),
          utcDate.getUTCDate(),
          utcDate.getUTCHours(),
          utcDate.getUTCMinutes(),
          utcDate.getUTCSeconds()
        );
    
        return format(utcTimestamp, formatString);
      } catch (err) {
        console.error('Error formatting date:', err, dateString);
        return 'Invalid Date';
      }
    };

  // Fetch Case Details
  const fetchCaseDetails = async () => {
    if (!caseId) {
      setError('No case selected');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const token = sessionStorage.getItem('auth_token');
      const res = await axiosInstance.get(`/ems/cases/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const actualCaseData = res.data?.data || res.data;
      setCaseData(actualCaseData);
      setError(null);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to load case details');
      if (err.response?.status === 401) navigate('/login');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCaseDetails();
  }, [caseId]);

  // Approve by Requester → Redirect after success
  const handleApprove = async () => {
    if (!window.confirm('Are you sure you want to approve this transfer case?')) return;

    try {
      setIsApproving(true);
      const formData = new URLSearchParams();
      formData.append('case_id', caseId);

      await axiosInstance.post('/ems/approved-by-requester', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
        },
      });

      alert('Case approved successfully!');
      // Redirect to the list page
      navigate('/ems/transfer-cases-final-approval');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to approve case');
    } finally {
      setIsApproving(false);
    }
  };

  // Reject by Requester → Redirect after success
  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    if (!window.confirm('Are you sure you want to reject this transfer case?')) return;

    try {
      setIsRejecting(true);
      const formData = new URLSearchParams();
      formData.append('case_id', caseId);
      formData.append('disapproval_notes_by_requester', rejectionReason.trim());

      await axiosInstance.post('/ems/disapproved-by-requester', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
        },
      });

      alert('Case rejected successfully!');
      setShowRejectionModal(false);
      setRejectionReason('');
      // Redirect to the list page
      navigate('/ems/transfer-cases-final-approval');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to reject case');
    } finally {
      setIsRejecting(false);
    }
  };

  // Document Handling
  const handleDocumentClick = (url) => {
    setSelectedDocument(url);
    setShowDocumentModal(true);
  };

  const renderDocumentContent = (url) => {
    const ext = url.split('.').pop().toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
      return <img src={url} alt="attachment" className="w-100" style={{ maxHeight: '500px', objectFit: 'contain' }} />;
    }
    if (['pdf', 'xml'].includes(ext)) {
      return <iframe src={url} width="100%" height="500px" title="Document" />;
    }
    return <p>Unsupported file type: {ext}</p>;
  };

  const getDocuments = () => {
    if (!caseData) return [];
    return ['documents1', 'documents2', 'documents3']
      .map(key => caseData[key])
      .filter(doc => doc && doc.trim() !== '');
  };

  // Table Fields (unchanged)
  const tableFields = [
    { label: 'ID', value: caseData?.id || 'N/A' },
    { label: 'Name', value: caseData?.case_name || 'N/A' },
    { label: 'Transfer From', value: caseData?.from_medical_center?.name || 'N/A' },
    { label: 'Transfer To', value: caseData?.medical_center?.name || 'N/A' },
    { label: 'Ambulance', value: caseData?.ambulance?.plate_number || 'N/A' },
    { label: 'Clinic No', value: caseData?.patient_situation?.clinic_no || 'N/A' },
    { label: 'Request Details', value: caseData?.patient_situation?.request_details || 'N/A' },
    { label: 'Escort Team(Physician)', value: caseData?.patient_situation?.escort_team?.escort_physician || 'N/A' },
    { label: 'Escort Team(Nurse)', value: caseData?.patient_situation?.escort_team?.escort_nurse || 'N/A' },
    { label: 'Escort Team(Paramedic)', value: caseData?.patient_situation?.escort_team?.escort_paramedic || 'N/A' },
    { label: 'Escort Team(Other)', value: caseData?.patient_situation?.escort_team?.other || 'N/A' },
    { label: 'Paramedics ID', value: caseData?.ems_member || 'N/A' },
    { label: 'Paramedics Name', value: caseData?.ems_member_name || 'N/A' },
    { label: 'Paramedics Status', value: caseData?.ems_team_status ? 'Busy' : 'Available' },
    { label: 'Nurse ID', value: caseData?.nurse?.id || 'N/A' },
    { label: 'Nurse Name', value: caseData?.nurse ? `${caseData.nurse.first_name || ''} ${caseData.nurse.last_name || ''}`.trim() : 'N/A' },
    { label: 'Doctor ID', value: caseData?.doctor?.id || 'N/A' },
    { label: 'Doctor Name', value: caseData?.doctor ? `${caseData.doctor.first_name || ''} ${caseData.doctor.last_name || ''}`.trim() : 'N/A' },
    { label: 'Doctor Approval Status', value: caseData?.doctor_approval_status === 1 ? 'Approved' : caseData?.doctor_approval_status === 'Rejected' ? 'Rejected' : 'Pending' },
    { label: 'Gender', value: caseData?.gender || 'N/A' },
    { label: 'Age', value: caseData?.age || 'N/A' },
    { label: 'Bracelet ID', value: caseData?.bracelet_id || 'N/A' },
    { label: 'Diagnose', value: caseData?.diagnose || 'N/A' },
    { label: 'Contact Number', value: caseData?.contact_number || 'N/A' },
    { label: 'Acknowledgment', value: caseData?.acknowledgment === 'Acknowledged' ? 'Yes' : 'No' },
    {
      label: 'Documents',
      value: (
        <p className="mb-0 fw-normal">
          <i className="ti ti-paperclip me-2"></i>
          {getDocuments().length > 0 ? (
            getDocuments().map((doc, i) => (
              <button key={i} className="btn btn-link p-0 me-3" onClick={() => handleDocumentClick(doc)}>
                <i className="ti ti-library-photo"></i>
              </button>
            ))
          ) : 'No documents'}
        </p>
      ),
    },
    { label: 'Case Date', value: caseData?.created_at ? formatCaseDate(caseData.created_at) : 'N/A' },
    {
      label: 'Patient Situation',
      value: (
        <button className="text-primary bg-transparent border-0 p-0" onClick={() => setIsPatientModalOpen(true)}>
          Click to view details
        </button>
      ),
    },
  ];

  return (
    <div id="main-wrapper">
      <DateTimeFormatFetcher />
      <LeftSidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        <Topbar toggleSidebar={toggleSidebar} />
        <div className="body-wrapper">
          <div className="container-fluid">
            <Settings ref={settingsRef} updateSetting={updateSetting} handleColorTheme={handleColorTheme} settings={settings} />

            {isLoading && (
              <div className="d-flex justify-content-center align-items-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}

            {error && !isLoading && (
              <div className="alert alert-danger text-center">
                {error}
                <button className="btn btn-primary btn-sm ms-3" onClick={fetchCaseDetails}>Retry</button>
              </div>
            )}

            {!isLoading && !error && caseData && (
              <div className="card w-100 position-relative overflow-hidden">
                <div className="px-4 py-3 border-bottom">
                  <h5 className="card-title fw-semibold mb-0">Transfer Case for Final Approval</h5>
                  <p className="card-subtitle mb-0 mt-1">
                    Review all details and give final approval or rejection
                  </p>
                </div>

                <div className="card-body p-4">
                  <div className="table-responsive rounded-2 mb-4">
                    <table className="table text-nowrap customize-table mb-0 align-middle">
                      <thead className="text-dark fs-4">
                        <tr>
                          <th><h6 className="fw-semibold mb-0">Field</h6></th>
                          <th><h6 className="fw-semibold mb-0">Value</h6></th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableFields.map((field, i) => (
                          <tr key={i}>
                            <td><p className="mb-0 fw-normal fw-semibold">{field.label}</p></td>
                            <td>{field.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Action Buttons */}
                  <div className="btn-panel pt-3">
                    <button
                      className="btn btn-success waves-effect waves-light btn-rounded me-3"
                      onClick={handleApprove}
                      disabled={isApproving}
                    >
                      {isApproving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Approving...
                        </>
                      ) : (
                        'Approve'
                      )}
                    </button>

                    <button
                      className="btn btn-danger waves-effect waves-light btn-rounded"
                      onClick={() => setShowRejectionModal(true)}
                      disabled={isRejecting}
                    >
                      {isRejecting ? 'Rejecting...' : 'Reject'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Document Modal */}
      {showDocumentModal && (
          <div
            className={`modal fade ${showDocumentModal ? 'show d-block' : ''}`}
            id="document-modal"
            tabIndex="-1"
            aria-labelledby="documentModalLabel"
            aria-modal="true"
            role="dialog"
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">Attachment</h4>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowDocumentModal(false)}
                  ></button>
                </div>

                <div className="modal-body">
                  {selectedDocument && renderDocumentContent(selectedDocument)}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn bg-danger-subtle text-danger"
                    onClick={() => setShowDocumentModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reject Transfer Case</h5>
                <button type="button" className="btn-close" onClick={() => setShowRejectionModal(false)}></button>
              </div>
              <div className="modal-body">
                <label className="form-label">Reason for Rejection <span className="text-danger">*</span></label>
                <textarea
                  className="form-control"
                  rows="5"
                  placeholder="Please provide a detailed reason for rejecting this transfer..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  disabled={isRejecting}
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-danger" onClick={() => setShowRejectionModal(false)} disabled={isRejecting}>
                  Cancel
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={handleReject}
                  disabled={isRejecting || !rejectionReason.trim()}
                >
                  {isRejecting ? 'Rejecting...' : 'Reject Case'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Patient Situation Modal */}
      <PatientSituationtransfercaseModal
        isOpen={isPatientModalOpen}
        onClose={() => setIsPatientModalOpen(false)}
        caseId={caseId}
        onSave={fetchCaseDetails}
      />
    </div>
  );
};

export default TransferCaseFinalApprovalDetails;